/**
 * Lightweight Supabase PostgREST client for the marketing site.
 * Uses fetch directly — no @supabase/supabase-js dependency needed.
 */
import { captureLandingEvent } from './telemetry'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined

function headers(upsert = false): HeadersInit {
  return {
    'Content-Type': 'application/json',
    apikey: SUPABASE_ANON_KEY ?? '',
    Authorization: `Bearer ${SUPABASE_ANON_KEY ?? ''}`,
    Prefer: upsert
      ? 'return=representation, resolution=merge-duplicates'
      : 'return=representation',
  }
}

function isConfigured(): boolean {
  return Boolean(SUPABASE_URL && SUPABASE_ANON_KEY)
}

/* ── Waitlist ── */

export interface WaitlistPayload {
  email: string
  name?: string
  phone?: string
  branches?: string
  referral?: string
  pharmacy_name?: string
  role?: string
  current_system?: string
  download_type?: string
  intent?: 'download' | 'demo'
  demo_status?: 'requested' | 'scheduled' | 'sent' | 'opened' | 'completed'
  demo_goal?: string
  demo_timeline?: string
  demo_availability?: string[]
  demo_timezone?: string
}

export interface WaitlistRow extends WaitlistPayload {
  id: string
  token: string
  created_at: string
}

export interface IntegrationCatalogItem {
  id: string
  key: string
  name: string
  connector_type: 'pms' | 'erp' | 'emr' | 'lmis' | 'spreadsheet' | 'manual' | 'special' | 'internal'
  status: 'live' | 'beta' | 'coming_soon' | 'deprecated'
  icon_slug: string | null
  sort_order: number
  is_featured: boolean
  description_short: string | null
}

export async function submitWaitlist(data: WaitlistPayload): Promise<WaitlistRow | null> {
  if (!isConfigured()) {
    console.warn('[fey] Supabase not configured — waitlist entry not persisted')
    return {
      ...data,
      id: crypto.randomUUID(),
      token: crypto.randomUUID().replace(/-/g, '').slice(0, 32),
      created_at: new Date().toISOString(),
    } as WaitlistRow
  }
  try {
    const insertOnce = async (payload: WaitlistPayload): Promise<WaitlistRow[] | null> => {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/waitlist`, {
        method: 'POST',
        headers: headers(true),
        body: JSON.stringify(payload),
      })

      if (!res.ok) {
        const errorText = await res.text().catch(() => '')
        throw new Error(`${res.status} ${res.statusText} ${errorText}`.trim())
      }

      return (await res.json()) as WaitlistRow[]
    }

    try {
      const rows = await insertOnce(data)
      return rows?.[0] ?? null
    } catch (firstError) {
      const fallbackPayload: WaitlistPayload = {
        email: data.email,
        name: data.name,
        phone: data.phone,
        branches: data.branches,
        referral: data.referral,
        download_type: data.intent === 'demo'
          ? `${data.download_type || 'Request'} (demo)`
          : data.download_type,
      }

      console.warn('[fey] submitWaitlist primary payload failed, retrying with legacy columns:', firstError)
      const rows = await insertOnce(fallbackPayload)
      return rows?.[0] ?? null
    }
  } catch (err) {
    console.error('[fey] submitWaitlist failed:', err)
    return null
  }
}

/* ── Waitlist email ── */

export async function sendWaitlistEmail(row: WaitlistRow): Promise<boolean> {
  if (!isConfigured()) {
    console.info('[fey] email: would send waitlist confirmation to', row.email)
    return true
  }
  try {
    const response = await fetch(`${SUPABASE_URL}/functions/v1/send-waitlist-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: SUPABASE_ANON_KEY ?? '',
        Authorization: `Bearer ${SUPABASE_ANON_KEY ?? ''}`,
      },
      body: JSON.stringify({
        email: row.email,
        name: row.name,
        phone: row.phone,
        branches: row.branches,
        referral: row.referral,
        download_type: row.download_type,
        current_system: row.current_system,
        intent: row.intent,
        demo_status: row.demo_status,
        demo_goal: row.demo_goal,
        demo_timeline: row.demo_timeline,
        demo_availability: row.demo_availability,
        demo_timezone: row.demo_timezone,
        token: row.token,
      }),
    })

    if (!response.ok) {
      const details = await response.text().catch(() => '')
      console.error(`[fey] sendWaitlistEmail failed (${response.status}): ${details || response.statusText}`)
      return false
    }
    return true
  } catch (err) {
    console.error('[fey] sendWaitlistEmail failed:', err)
    return false
  }
}

/* ── Events ── */

export async function trackEvent(
  token: string | null,
  event: string,
  metadata: Record<string, unknown> = {},
): Promise<void> {
  captureLandingEvent(event, metadata)

  if (!token) return
  if (!isConfigured()) {
    console.info(`[fey] track: ${event}`, metadata)
    return
  }
  try {
    await fetch(`${SUPABASE_URL}/rest/v1/waitlist_events`, {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify({ token, event, metadata }),
    })
  } catch (err) {
    console.error('[fey] trackEvent failed:', err)
  }
}

/* ── Integration catalog ── */

// Auto-generated at deploy time by scripts/generate-landing-catalog.ts.
// Falls back to empty array if the generated file doesn't exist yet (dev mode).
const generatedCatalogModule = Object.values(
  import.meta.glob<{ default: IntegrationCatalogItem[] }>('./generated/catalog.json', { eager: true }),
)[0]

const FALLBACK_CATALOG: IntegrationCatalogItem[] = [
  { id: '1', key: 'csv', name: 'CSV Import', connector_type: 'spreadsheet', status: 'live', icon_slug: null, sort_order: 1, is_featured: true, description_short: 'Bulk upload inventory and sales data' },
  { id: '2', key: 'google_sheets', name: 'Google Sheets', connector_type: 'spreadsheet', status: 'live', icon_slug: null, sort_order: 2, is_featured: true, description_short: 'Sync stock lists and procurement plans' },
  { id: '3', key: 'excel_online', name: 'Excel Online', connector_type: 'spreadsheet', status: 'beta', icon_slug: null, sort_order: 3, is_featured: false, description_short: 'Microsoft 365 spreadsheet integration' },
  { id: '4', key: 'api', name: 'REST API', connector_type: 'internal', status: 'live', icon_slug: null, sort_order: 4, is_featured: true, description_short: 'Build custom integrations and webhooks' },
  { id: '5', key: 'pms', name: 'Pharmacy Management', connector_type: 'pms', status: 'coming_soon', icon_slug: null, sort_order: 5, is_featured: false, description_short: 'PioneerRx, Computer-Rx, QS/1, and more' },
  { id: '6', key: 'whatsapp', name: 'WhatsApp', connector_type: 'special', status: 'beta', icon_slug: null, sort_order: 6, is_featured: false, description_short: 'Send quotes and order updates to suppliers' },
  { id: '7', key: 'quickbooks', name: 'QuickBooks', connector_type: 'erp', status: 'coming_soon', icon_slug: null, sort_order: 7, is_featured: false, description_short: 'Sync procurement spend with accounting' },
  { id: '8', key: 'msupply', name: 'mSupply', connector_type: 'lmis', status: 'coming_soon', icon_slug: null, sort_order: 8, is_featured: false, description_short: 'Health supply chain management integration' },
]

const GENERATED_CATALOG: IntegrationCatalogItem[] = generatedCatalogModule?.default ?? FALLBACK_CATALOG

export async function listLandingIntegrationCatalog(): Promise<IntegrationCatalogItem[]> {
  if (!isConfigured()) {
    return FALLBACK_CATALOG
  }

  try {
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 3000)

    const response = await fetch(`${SUPABASE_URL}/functions/v1/integrations-setup/catalog?surface=landing`, {
      method: 'GET',
      headers: {
        apikey: SUPABASE_ANON_KEY ?? '',
        Authorization: `Bearer ${SUPABASE_ANON_KEY ?? ''}`,
      },
      signal: controller.signal,
    })
    clearTimeout(timeout)

    if (!response.ok) {
      const details = await response.text().catch(() => '')
      throw new Error(`catalog fetch failed (${response.status}): ${details || response.statusText}`)
    }

    const body = await response.json() as { data?: IntegrationCatalogItem[]; error?: unknown }
    const live = Array.isArray(body.data) ? body.data : []
    return live.length > 0 ? live : GENERATED_CATALOG
  } catch (error) {
    console.error('[fey] listLandingIntegrationCatalog failed, using fallback:', error)
    return GENERATED_CATALOG
  }
}

/* ── Changelog ── */

export interface ChangelogEntry {
  version: string
  date: string
  added: string[]
  changed: string[]
  deprecated: string[]
  fixed: string[]
  security: string[]
  removed: string[]
}

export interface ChangelogResponse {
  releases: ChangelogEntry[]
  latestVersion: string
  latestDate: string
}

const FALLBACK_CHANGELOG: ChangelogResponse = {
  releases: [
    {
      version: '1.2.0',
      date: '2026-02-15',
      added: [
        'AI Assistant Integration - New natural language interface for querying inventory and creating purchase orders',
        'WhatsApp Supplier Notifications - Send quote requests and order updates directly via WhatsApp',
        'Expiry Risk Alerts - Configurable alerts for items expiring within 7/14/30 days',
        "Dashboard Widgets - New at-a-glance widgets showing pending orders, low-stock items, and today's activity",
      ],
      changed: [
        'Improved Quote Comparison - Side-by-side comparison view for supplier quotes with better visualization',
        'Faster Inventory Sync - Reduced sync time by 40% for large inventories (10,000+ SKUs)',
        'Mobile App Redesign - Updated mobile interface with bottom navigation and improved touch targets',
      ],
      deprecated: [],
      fixed: [
        'Resolved sync timeout issue when importing large CSV files',
        'Fixed PDF export formatting for purchase orders',
        'Corrected currency display in the savings calculator for NGN market',
      ],
      security: [
        'Updated SSL certificates for all API endpoints',
      ],
      removed: [],
    },
    {
      version: '1.1.0',
      date: '2026-01-28',
      added: [
        'Supplier Catalog Search - Search and filter supplier product catalogs directly within Fey',
        'Bulk Order Creation - Create multiple purchase orders from a single stock list',
        'Inventory Audit Trail - Complete history of all stock changes with user attribution',
      ],
      changed: [
        'Onboarding Flow Redesign - Simplified 5-step onboarding for new pharmacies',
        'List Management - Improved list creation with templates for common pharmacy scenarios',
      ],
      deprecated: [],
      fixed: [
        'Fixed timezone handling for scheduling orders',
        'Resolved issue with duplicate supplier entries',
      ],
      security: [],
      removed: [],
    },
    {
      version: '1.0.0',
      date: '2026-01-15',
      added: [
        'Initial Public Release - Core procurement platform for pharmacy stock management',
        'Inventory Tracking - Real-time stock levels with automatic low-stock alerts',
        'Supplier Management - Directory of suppliers with contact and pricing information',
        'Quote Request System - Streamlined workflow for requesting and comparing supplier quotes',
        'Purchase Order Management - Create, track, and manage purchase orders',
        'Integration Ecosystem - Connectors for major pharmacy management systems (DispensaryOS, mSupply, RxWorks)',
        'Pricing Calculator - Estimate cost savings from better procurement practices',
        'Multi-branch Support - Manage inventory across multiple pharmacy locations',
        'Export & Reporting - Export data to CSV and generate standard reports',
      ],
      changed: [],
      deprecated: [],
      fixed: [],
      security: [],
      removed: [],
    },
    {
      version: '0.9.0',
      date: '2025-12-20',
      added: [
        'Beta Release - Limited availability for early adopter pharmacies',
        'Waitlist System - Sign up for early access and product updates',
      ],
      changed: [
        'Iterative improvements based on beta user feedback',
      ],
      deprecated: [],
      fixed: [],
      security: [],
      removed: [],
    },
    {
      version: '0.1.0',
      date: '2025-11-01',
      added: [
        'Internal Alpha - Initial development milestone',
        'Core data models and API infrastructure',
      ],
      changed: [],
      deprecated: [],
      fixed: [],
      security: [],
      removed: [],
    },
  ],
  latestVersion: '1.2.0',
  latestDate: '2026-02-15',
}

export async function fetchChangelog(): Promise<ChangelogResponse> {
  if (!isConfigured()) {
    return FALLBACK_CHANGELOG
  }

  try {
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 5000)

    const response = await fetch(`${SUPABASE_URL}/functions/v1/changelog-parse`, {
      method: 'GET',
      headers: {
        apikey: SUPABASE_ANON_KEY ?? '',
        Authorization: `Bearer ${SUPABASE_ANON_KEY ?? ''}`,
      },
      signal: controller.signal,
    })
    clearTimeout(timeout)

    if (!response.ok) {
      const details = await response.text().catch(() => '')
      throw new Error(`changelog fetch failed (${response.status}): ${details || response.statusText}`)
    }

    const body = await response.json() as ChangelogResponse
    if (body.releases && body.releases.length > 0) {
      return body
    }
    return FALLBACK_CHANGELOG
  } catch (error) {
    console.error('[fey] fetchChangelog failed, using fallback:', error)
    return FALLBACK_CHANGELOG
  }
}
